import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { RefreshTokenService } from './refresh-token.service';
import { inject } from '@angular/core';
import { environment } from '../../environment';
import { GrunddatenService } from '../grundlagen/grunddaten.service';
import { ApiError } from '../grundlagen/GlobalErrorHandler';

// Konstanten für bessere Wartbarkeit
const EXCLUDED_AUTH_ENDPOINTS = ['authenticate/', 'grundlagen/ErrorLog', 'system/version'] as const;

const EXCLUDED_CONTENT_TYPE_ENDPOINTS = ['Dokumente/Upload'] as const;

/**
 * Prüft ob die URL einen der ausgeschlossenen Endpoints enthält
 */
function isExcludedEndpoint(url: string, endpoints: readonly string[]): boolean {
  const baseUrl = environment.baseUrl();
  return endpoints.some((endpoint) => url.startsWith(baseUrl + endpoint));
}

/**
 * Extrahiert die Fehlermeldung aus verschiedenen Error-Strukturen
 */
function extractErrorMessage(error: HttpErrorResponse): string {
  // Priorität: error.error direkt -> error.error.message -> error.error.bezeichnung -> error.message
  if (typeof error.error === 'string' && error.error.trim() !== '') {
    return error.error;
  }

  if (error.error?.message) {
    return error.error.message;
  }

  if (error.error?.bezeichnung) {
    return error.error.bezeichnung;
  }

  if (error.message) {
    return error.message;
  }

  return 'Ein unbekannter Fehler ist aufgetreten';
}

/**
 * Fügt Authorization Header hinzu
 */
function addAuthorizationHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${token}`),
  });
}

/**
 * Fügt Content-Type Header hinzu
 */
function addContentTypeHeader(request: HttpRequest<any>): HttpRequest<any> {
  return request.clone({
    headers: request.headers.set('Content-Type', 'application/json'),
  });
}

/**
 * Bearer Token Interceptor für HTTP-Requests
 * - Fügt Authorization Header hinzu
 * - Verwaltet Token-Refresh
 * - Behandelt 401 Errors mit Redirect zu Login
 */
export const bearerInterceptor: HttpInterceptorFn = (request, next) => {
  const grundlagen = inject(GrunddatenService);
  const refreshTokenService = inject(RefreshTokenService);

  // Prüfe ob Endpoint von Authentifizierung ausgeschlossen ist
  const requiresAuth = !isExcludedEndpoint(request.url, EXCLUDED_AUTH_ENDPOINTS);

  if (requiresAuth) {
    // Content-Type Header hinzufügen (außer für Upload-Endpoints)
    if (!isExcludedEndpoint(request.url, EXCLUDED_CONTENT_TYPE_ENDPOINTS)) {
      request = addContentTypeHeader(request);
    }

    // Token-Validierung und ggf. Refresh
    let token = localStorage.getItem('accessToken');

    if (!grundlagen.CheckTokenAktuell()) {
      localStorage.setItem('requestURL', request.url);
      token = refreshTokenService.refreshingTokens();
    }

    // Authorization Header hinzufügen
    if (token) {
      request = addAuthorizationHeader(request, token);
    }
  }

  // Request ausführen mit Error Handling
  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // 401 Unauthorized - Redirect zu Login
      if (error.status === 401) {
        if (requiresAuth) {
          localStorage.setItem('requestURL', request.url);
          window.location.href = '/login';
        }
        // Request nochmal durchreichen für weitere Verarbeitung
        return next(request).pipe(catchError(() => throwError(() => new ApiError('Unauthorized', 401))));
      }

      // Andere Fehler - Fehlermeldung extrahieren und werfen
      const errorMsg = extractErrorMessage(error);
      return throwError(() => new ApiError(errorMsg, error.status));
    }),
  );
};
